import React, { useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  CreditCard,
  DollarSign,
  Globe,
  Code,
  FileText,
  Settings,
  ChevronRight,
  X,
  Package,
} from 'lucide-react';
import styles from './Sidebar.module.css';
import logo from "../../assets/logo.png";

interface NavItem {
  icon: React.ElementType;
  label: string;
  path: string;
  badge?: string | null;
}

const mainMenuItems: NavItem[] = [
  { icon: LayoutDashboard, label: 'Dashboard',             path: '/dashboard',               badge: null },
  { icon: Users,           label: 'Tenants',               path: '/dashboard/tenants',       badge: '24' },
  { icon: CreditCard,      label: 'Subscriptions / Plans', path: '/dashboard/subscriptions', badge: null },
  { icon: DollarSign,      label: 'Payments',              path: '/dashboard/payments',      badge: '3'  },
  { icon: Package,      label: 'Manage Modules',        path: '/dashboard/modules',      badge: null  },
];

const systemMenuItems: NavItem[] = [
  { icon: Globe,    label: 'Domain Provisioning', path: '/dashboard/domain-provisioning', badge: null },
  { icon: Code,     label: 'API Integrations',    path: '/dashboard/api-integrations',    badge: '!'  },
  { icon: FileText, label: 'System Logs',         path: '/dashboard/system-logs',         badge: null },
  { icon: Settings, label: 'Settings',            path: '/dashboard/settings',            badge: null },
];

const FlyGDSLogo: React.FC = () => (
  <img src={logo} alt="FlyGDS" className={styles.logoImg} />
);

interface NavItemButtonProps {
  item: NavItem;
  isActive: boolean;
  onClick: (path: string) => void;
}

const NavItemButton: React.FC<NavItemButtonProps> = ({ item, isActive, onClick }) => {
  const btnRef = useRef<HTMLButtonElement>(null);

  const handleClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    const btn = btnRef.current;
    if (btn) {
      const rect = btn.getBoundingClientRect();
      const ripple = document.createElement('span');
      const size = Math.max(rect.width, rect.height);
      ripple.style.cssText = `
        position: absolute;
        border-radius: 50%;
        background: rgba(29, 78, 216, 0.18);
        pointer-events: none;
        width: ${size}px;
        height: ${size}px;
        left: ${e.clientX - rect.left - size / 2}px;
        top: ${e.clientY - rect.top - size / 2}px;
        animation: rippleOut 0.55s ease-out forwards;
      `;
      btn.appendChild(ripple);
      setTimeout(() => ripple.remove(), 600);
    }
    onClick(item.path);
  }, [item.path, onClick]);

  const itemClass = [
    styles.navItem,
    isActive ? styles.navItemActive : '',
  ].filter(Boolean).join(' ');

  const iconClass = [
    styles.navIcon,
    isActive ? styles.navIconActive : '',
  ].filter(Boolean).join(' ');

  const badgeClass = [
    styles.badge,
    item.badge === '!' ? styles.badgeAlert : styles.badgeCount,
  ].filter(Boolean).join(' ');

  return (
    <button
      ref={btnRef}
      className={itemClass}
      onClick={handleClick}
      aria-current={isActive ? 'page' : undefined}
    >
      <span className={iconClass} aria-hidden="true">
        <item.icon size={16} strokeWidth={isActive ? 2.2 : 1.8} />
      </span>

      <span className={styles.navLabelText}>{item.label}</span>

      {item.badge && (
        <span className={badgeClass} aria-label={`${item.badge} notifications`}>
          {item.badge}
        </span>
      )}
    </button>
  );
};

const Sidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = React.useState(false);

  const closeMobile = useCallback(() => setMobileOpen(false), []);

  const handleNavClick = useCallback((path: string) => {
    navigate(path);
    setMobileOpen(false);
  }, [navigate]);

  const isActive = (path: string) => {
    if (path === '/dashboard') return location.pathname === '/dashboard';
    return location.pathname.startsWith(path);
  };

  const sidebarClass = [
    styles.sidebar,
    mobileOpen ? styles.sidebarOpen : '',
  ].filter(Boolean).join(' ');

  return (
    <>
      <button
        className={`${styles.mobileToggle} ${mobileOpen ? styles.hamburgerOpen : ''}`}
        onClick={mobileOpen ? closeMobile : () => setMobileOpen(true)}
        aria-label={mobileOpen ? 'Close navigation' : 'Open navigation'}
        aria-expanded={mobileOpen}
      >
        <span className={styles.hamburgerLine} />
        <span className={styles.hamburgerLine} />
        <span className={styles.hamburgerLine} />
      </button>

      {mobileOpen && (
        <div className={styles.backdrop} onClick={closeMobile} aria-hidden="true" />
      )}

      <aside className={sidebarClass} aria-label="Platform navigation">
        <div className={styles.logoZone}>
          <FlyGDSLogo />
          <div className={styles.logoTextGroup}>
            <div className={styles.brandTag}>Platform Admin</div>
          </div>
          <button className={styles.closeBtn} onClick={closeMobile} aria-label="Close navigation">
            <X size={16} strokeWidth={2.5} />
          </button>
        </div>

        <nav className={styles.navArea} aria-label="Main navigation">
          <div className={styles.navSection}>
            <p className={styles.sectionLabel} aria-hidden="true">Main</p>
            <ul className={styles.navList} role="list">
              {mainMenuItems.map((item) => (
                <li key={item.path}>
                  <NavItemButton
                    item={item}
                    isActive={isActive(item.path)}
                    onClick={handleNavClick}
                  />
                </li>
              ))}
            </ul>
          </div>

          <div className={styles.divider} aria-hidden="true" />

          <div className={`${styles.navSection} ${styles.systemSection}`}>
            <p className={styles.sectionLabel} aria-hidden="true">System</p>
            <ul className={styles.navList} role="list">
              {systemMenuItems.map((item) => (
                <li key={item.path}>
                  <NavItemButton
                    item={item}
                    isActive={isActive(item.path)}
                    onClick={handleNavClick}
                  />
                </li>
              ))}
            </ul>
          </div>
        </nav>

        <div className={styles.adminProfile} role="button" tabIndex={0} aria-label="Admin profile menu">
          <div className={styles.adminAvatarWrap}>
            <div className={styles.adminAvatar} aria-hidden="true">FA</div>
            <span className={styles.statusDot} aria-label="Online" />
          </div>
          <div className={styles.adminInfo}>
            <div className={styles.adminName}>FlyGDS Admin</div>
            <div className={styles.adminRole}>Platform Admin</div>
          </div>
          <span className={styles.adminChevron} aria-hidden="true">
            <ChevronRight size={15} strokeWidth={2} />
          </span>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;